import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Lock, Mail, Copy, RefreshCw, Send, ExternalLink, ShieldCheck, Loader2,
  Wallet, LogOut, CheckCircle2, Clock, XCircle, Sparkles, ArrowRight,
  Download, Ban, ArrowDown, Webhook, Key, Eye, EyeOff, Image as ImageIcon,
} from "lucide-react";

/** Xchangely Pay — merchant portal. B2B clients sign up, get approved,
 *  then generate hosted-checkout payment links scoped to their own account.
 *
 *  All public-facing copy must NEVER reference the upstream payment provider
 *  by name (the integration is white-labelled).
 */

const TOK_KEY = "xpay_token";
// Official InstaXchange FTD fiat list (Jun 2026). GBP is NOT supported.
const FIAT   = ["EUR", "USD", "AUD", "CAD", "CHF", "SEK", "NOK", "DKK",
                "PLN", "CZK", "RON", "TRY", "AED", "SAR", "QAR", "BRL",
                "MXN", "CLP", "ZAR", "JPY", "HKD", "SGD", "TWD", "THB",
                "PHP", "IDR", "VND", "NZD"];

/** FastAPI / Pydantic give us 422s with `detail` shaped as a list of error
 *  objects (`[{msg, loc, type}, ...]`). Naively shoving that into a toast
 *  renders "[object Object]" — which our customers were perceiving as a
 *  silent blank screen. Always run upstream errors through this before
 *  surfacing them.
 */
const humanizeApiError = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((it) => it?.msg || it?.message || String(it)).join(". ");
  }
  if (typeof d?.detail === "string") return d.detail;
  if (typeof d?.message === "string") return d.message;
  return fallback;
};
const CRYPTO = ["USDC_POLYGON"]; // settlement is fixed to USDC on Polygon

/**
 * Methods supported by the upstream payment partner. `forceFrom` pins the
 * `fromCurrency` automatically when a method only works in a single fiat
 * (PayID is AUD-only; Klarna SEK-only). The merchant doesn't need to think
 * about the matching — the UI swaps the currency for them.
 */
const METHODS = [
  { v: "card",       l: "Credit / debit card" },
  { v: "paypal",     l: "PayPal" },
  { v: "klarna",     l: "Klarna",       forceFrom: "SEK", note: "SEK only" },
  { v: "payid",      l: "PayID",        forceFrom: "AUD", note: "AUD only" },
  { v: "ideal",      l: "iDEAL",        note: "EUR · NL" },
  // 🩹 Iter58 — Bancontact + EPS hidden until InstaXchange enables them
  // on our merchant account ((superseded account ref)). Live audit on 2026-02-23
  // confirmed all other rails work end-to-end; these two return
  // "Payment method '<name>' is not approved for merchant account".
  // Re-enable here once InstaXchange support confirms activation.
  // { v: "bancontact", l: "Bancontact",   note: "EUR · BE" },
  { v: "apple-pay",  l: "Apple Pay" },
  { v: "google-pay", l: "Google Pay" },
  { v: "sepa",       l: "SEPA bank transfer", note: "EUR" },
  // { v: "eps",        l: "EPS",          note: "EUR · AT" },
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
const statusTone = (s) => {
  if (!s) return { tone: "neutral", icon: Clock };
  const k = (s + "").toLowerCase();
  if (["completed", "settled", "paid"].includes(k))         return { tone: "ok",   icon: CheckCircle2 };
  if (["failed", "expired", "rejected", "cancelled"].includes(k)) return { tone: "bad",  icon: XCircle };
  return { tone: "wait", icon: Clock };
};
const toneClass = {
  ok:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  bad:  "bg-rose-50 text-rose-700 border-rose-200",
  wait: "bg-amber-50 text-amber-700 border-amber-200",
  neutral: "bg-muted text-foreground border-border",
};


// ============================================================
// Logged-out shell — login / signup
// ============================================================
function AuthShell({ onAuthed }) {
  const [mode, setMode] = useState("login"); // 'login' | 'signup' | 'thanks'
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [contactName, setContactName] = useState("");

  const doLogin = async () => {
    if (!email.trim() || !password) { toast.error("Enter your email and password"); return; }
    setBusy(true);
    try {
      const r = await api.post("/xpay/merchant/login", { email: email.trim(), password });
      localStorage.setItem(TOK_KEY, r.data.token);
      onAuthed(r.data.merchant);
      toast.success(`Welcome back, ${r.data.merchant.business_name || "merchant"}`);
    } catch (e) {
      toast.error(humanizeApiError(e, "Login failed"));
    } finally { setBusy(false); }
  };
  const doSignup = async () => {
    if (!email.trim() || !password || !businessName.trim()) {
      toast.error("Email, password and business name are required"); return;
    }
    if (password.length < 10) {
      toast.error("Password needs to be at least 10 characters"); return;
    }
    setBusy(true);
    try {
      await api.post("/xpay/merchant/signup", {
        email:         email.trim(),
        password,
        business_name: businessName.trim(),
        contact_name:  contactName.trim(),
      });
      setMode("thanks");
    } catch (e) {
      toast.error(humanizeApiError(e, "Sign-up failed"));
    } finally { setBusy(false); }
  };

  if (mode === "thanks") {
    return (
      <div className="w-full max-w-md xch-card p-7 text-center" data-testid="xpay-thanks">
        <div className="w-14 h-14 rounded-2xl mx-auto bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
          <Mail className="w-6 h-6 text-emerald-700" />
        </div>
        <h2 className="font-display font-bold text-xl mb-1.5">You&apos;re on the list</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Thanks for signing up. We review every new merchant by hand — usually
          within one business day. You&apos;ll get an email at <strong>{email}</strong> the
          moment your account is approved.
        </p>
        <Button variant="outline" className="rounded-xl gap-1.5 w-full"
                onClick={() => { setMode("login"); setPassword(""); }}
                data-testid="xpay-back-to-login">
          Back to sign in <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md xch-card p-7" data-testid="xpay-auth-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-indigo-50 border border-indigo-200">
          <Sparkles className="w-5 h-5 text-indigo-700" />
        </div>
        <div>
          <div className="font-display font-bold text-lg">Xchangely Pay</div>
          <div className="text-[11px] text-muted-foreground">Generate crypto payment links for your customers</div>
        </div>
      </div>

      <div className="flex p-1 bg-muted rounded-xl mb-5" data-testid="xpay-mode-toggle">
        <button onClick={() => setMode("login")}
                className={`flex-1 text-xs font-semibold py-1.5 rounded-lg transition-colors
                  ${mode === "login" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                data-testid="xpay-tab-login">Sign in</button>
        <button onClick={() => setMode("signup")}
                className={`flex-1 text-xs font-semibold py-1.5 rounded-lg transition-colors
                  ${mode === "signup" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
                data-testid="xpay-tab-signup">Create account</button>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="xpay-email" />
        </div>
        {mode === "signup" && (
          <>
            <div>
              <Label className="text-xs">Business name</Label>
              <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Acme Ltd"
                      data-testid="xpay-business-name" />
            </div>
            <div>
              <Label className="text-xs">Your name (optional)</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)}
                      data-testid="xpay-contact-name" />
            </div>
          </>
        )}
        <div>
          <Label className="text-xs">Password</Label>
          <Input type="password"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (mode === "login" ? doLogin() : doSignup())}
                  data-testid="xpay-password" />
          {mode === "signup" && (
            <div className="text-[10px] text-muted-foreground mt-1" data-testid="xpay-password-hint">
              Minimum 10 characters with at least one letter and one digit.
            </div>
          )}
        </div>

        <Button onClick={mode === "login" ? doLogin : doSignup} disabled={busy}
                className="w-full mt-3 rounded-xl gap-1.5"
                data-testid={mode === "login" ? "xpay-login-btn" : "xpay-signup-btn"}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                : (mode === "login" ? <ShieldCheck className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
          {mode === "login" ? "Sign in" : "Create account"}
        </Button>
        {mode === "login" && (
          <a href="/forgot-password?audience=merchant"
              className="block text-center text-xs text-muted-foreground hover:text-foreground mt-2 underline-offset-2 hover:underline"
              data-testid="xpay-forgot-password-link">
            Forgot your password?
          </a>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground text-center mt-5">
        By signing up you agree to our <a href="/legal/terms" className="underline">Terms</a>{" "}
        and <a href="/legal/privacy" className="underline">Privacy Policy</a>.
      </p>
    </div>
  );
}


// ============================================================
// Pending-approval gate
// ============================================================
function PendingGate({ merchant, onLogout, onRefresh }) {
  return (
    <div className="w-full max-w-md xch-card p-7" data-testid="xpay-pending">
      <div className="w-14 h-14 rounded-2xl mx-auto bg-amber-50 border border-amber-200 flex items-center justify-center mb-4">
        <Clock className="w-6 h-6 text-amber-700" />
      </div>
      <h2 className="font-display font-bold text-xl mb-1.5 text-center">Account pending review</h2>
      <p className="text-sm text-muted-foreground text-center mb-5">
        Hi {merchant.contact_name || merchant.business_name || "there"} — your
        application is being reviewed by our team. We&apos;ll email{" "}
        <strong>{merchant.email}</strong> the moment it&apos;s approved (usually within
        one business day).
      </p>
      <div className="flex gap-2">
        <Button onClick={onRefresh} variant="outline" className="flex-1 rounded-xl gap-1.5"
                data-testid="xpay-pending-refresh">
          <RefreshCw className="w-4 h-4" /> Check again
        </Button>
        <Button onClick={onLogout} variant="ghost" className="rounded-xl gap-1.5"
                data-testid="xpay-pending-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}


// ============================================================
// Dashboard
// ============================================================
function Dashboard({ merchant, onLogout, onRefreshMerchant }) {
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, in_progress: 0, failed: 0 });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    fromAmount: "100",
    fromCurrency: "EUR",
    // 🔒 Iter45 #15 — must be NETWORK-QUALIFIED. Bare "USDC" dead-ends
    // upstream because Banxa can't pick a chain. USDC_POLYGON is broadly
    // card-supported and cheap. Until we surface a dropdown of allowed
    // routes (waiting on the InstaXchange route matrix), the default is
    // hard-coded but settable per merchant.
    toCurrency: "USDC_POLYGON",
    method: "card", email: "", reference: "",
  });
  const [lastLink, setLastLink] = useState(null);

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/xpay/merchant/links"),
        api.get("/xpay/merchant/stats"),
      ]);
      setLinks(a.data?.links || []);
      setStats(b.data || stats);
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); }
    }
  };
  useEffect(() => { loadAll(); }, []);

  const generate = async () => {
    if (!form.fromAmount) { toast.error("Please enter an amount"); return; }
    setBusy(true);
    try {
      const r = await api.post("/xpay/merchant/links", {
        fromAmount:   Number(form.fromAmount),
        fromCurrency: form.fromCurrency,
        toCurrency:   form.toCurrency,
        method:       form.method,
        email:        form.email.trim() || undefined,
        reference:    form.reference.trim() || undefined,
      });
      setLastLink(r.data);
      toast.success("Payment link ready");
      await loadAll();
    } catch (e) {
      toast.error(humanizeApiError(e, "Couldn't generate the link"));
    } finally { setBusy(false); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };

  const refreshOne = async (id) => {
    try {
      await api.get(`/xpay/merchant/links/${id}/status`);
      await loadAll();
      toast.success("Status updated");
    } catch (e) { toast.error("Could not refresh status"); }
  };

  const cancelLink = async (id) => {
    if (!window.confirm("Cancel this payment link? The customer won't be able to pay it any more.")) return;
    try {
      await api.delete(`/xpay/merchant/links/${id}`);
      await loadAll();
      toast.success("Link cancelled");
    } catch (e) {
      toast.error(humanizeApiError(e, "Could not cancel — already settled?"));
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get(`/xpay/merchant/links.csv`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `xchangely-pay-links-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Couldn't export CSV"); }
  };

  // Status helpers (used by row UI to decide whether the cancel button shows)
  const isTerminal = (s) => {
    const k = (s || "").toLowerCase();
    return ["completed", "settled", "paid", "failed", "expired", "rejected", "refunded", "cancelled"].includes(k);
  };

  // ---------- Webhook settings panel state ----------
  const [callbackUrl, setCallbackUrl] = useState(merchant.callback_url || "");
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [secretBusy, setSecretBusy] = useState(false);

  // ---------- Brand & receipts panel state ----------
  const [logoUrl, setLogoUrl] = useState(merchant.logo_url || "");
  const [supportEmail, setSupportEmail] = useState(merchant.support_email || "");
  const [brandBusy, setBrandBusy] = useState(false);
  // Note: we intentionally don't track "did the preview image load" as
  // React state — the <img onError> handler hides the broken affordance
  // via style.display directly, which sidesteps the eslint
  //  rule and keeps the preview reactive.
  const saveBrand = async () => {
    // Surface a quick client-side check so the failure path doesn't even
    // touch the server — the server still validates definitively.
    const u = logoUrl.trim();
    if (u && !u.toLowerCase().startsWith("https://")) {
      toast.error("Logo URL must start with https://");
      return;
    }
    setBrandBusy(true);
    try {
      await api.patch("/xpay/merchant/me", {
        // PATCH /me is the same endpoint we use for callback_url; we always
        // re-send the current callback so it doesn't get cleared by an
        // empty default. logo_url=null means "leave alone" — we send the
        // string instead so empty clears the field.
        callback_url:  callbackUrl.trim(),
        logo_url:      u,
        support_email: supportEmail.trim() || null,
      });
      toast.success("Brand settings saved");
      onRefreshMerchant && onRefreshMerchant();
    } catch (e) {
      toast.error(humanizeApiError(e, "Could not save brand settings"));
    } finally { setBrandBusy(false); }
  };
  useEffect(() => {
    let stop = false;
    api.get("/xpay/merchant/me/webhook-secret")
      .then((r) => { if (!stop) setWebhookSecret(r.data?.webhook_secret || null); })
      .catch(() => { /* swallow — display will show '—' */ });
    return () => { stop = true; };
  }, []);
  const saveCallbackUrl = async () => {
    setSecretBusy(true);
    try {
      await api.patch("/xpay/merchant/me", { callback_url: callbackUrl.trim() });
      toast.success("Callback URL saved");
      onRefreshMerchant && onRefreshMerchant();
    } catch (e) {
      toast.error(humanizeApiError(e, "Could not save callback URL"));
    } finally { setSecretBusy(false); }
  };
  const rotateSecret = async () => {
    if (!window.confirm("Rotating the secret will INVALIDATE the existing one. Any webhook signatures in-flight will fail verification on your side until you redeploy with the new value. Continue?")) return;
    setSecretBusy(true);
    try {
      const r = await api.post("/xpay/merchant/me/webhook-secret/rotate");
      setWebhookSecret(r.data?.webhook_secret || null);
      setShowSecret(true);
      toast.success("Secret rotated — copy it now");
    } catch (e) { toast.error("Rotate failed"); }
    finally { setSecretBusy(false); }
  };
  const copySecret = async () => {
    if (!webhookSecret) return;
    try { await navigator.clipboard.writeText(webhookSecret); toast.success("Secret copied"); }
    catch { toast.error("Copy failed"); }
  };
  const maskedSecret = (s) =>
    s ? s.slice(0, 7) + "•".repeat(Math.max(0, s.length - 14)) + s.slice(-7) : "—";

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-indigo-50 border border-indigo-200">
              <Sparkles className="w-4 h-4 text-indigo-700" />
            </div>
            <div>
              <div className="font-display font-bold leading-tight">Xchangely Pay</div>
              <div className="text-[10px] text-muted-foreground leading-tight">{merchant.business_name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {merchant.status === "live" ? (
              <Badge variant="outline"
                      className="hidden xs:inline-flex text-[10px] uppercase tracking-wider gap-1.5 bg-emerald-50 border-emerald-200 text-emerald-700"
                      data-testid="xpay-account-pill">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-600"></span>
                </span>
                Live
              </Badge>
            ) : (
              <Badge variant="outline"
                      className="hidden xs:inline-flex text-[10px] uppercase tracking-wider gap-1.5 bg-amber-50 border-amber-200 text-amber-800"
                      data-testid="xpay-account-pill">
                <Clock className="w-3 h-3" /> Awaiting activation
              </Badge>
            )}
            <Button onClick={onLogout} variant="ghost" size="sm" className="rounded-xl gap-1.5"
                    data-testid="xpay-logout-btn">
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-5 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7" data-testid="xpay-stats">
          {[
            { l: "Total links",  v: stats.total,        c: "" },
            { l: "Completed",    v: stats.completed,    c: "text-emerald-700" },
            { l: "In progress",  v: stats.in_progress,  c: "text-amber-700" },
            { l: "Failed",       v: stats.failed,       c: "text-rose-700" },
          ].map((s) => (
            <div key={s.l} className="xch-card p-3 sm:p-4">
              <div className={`text-xl sm:text-2xl font-display font-bold ${s.c}`}>{s.v}</div>
              <div className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Awaiting-activation banner — shows when account is `approved` but not yet `live`. */}
        {merchant.status !== "live" && (
          <div className="xch-card p-4 mb-5 border-amber-200 bg-amber-50/70" data-testid="xpay-awaiting-go-live">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 text-amber-700" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-amber-900">
                  Awaiting activation by Xchangely
                </div>
                <p className="text-xs text-amber-900/85 mt-1 leading-relaxed">
                  Your account is fully approved — we&apos;re just doing the final
                  go-live check. You&apos;ll be able to create payment links the
                  moment our team flips you to <strong>LIVE</strong>. We&apos;ll email
                  you at <strong>{merchant.email}</strong> the moment that
                  happens (usually within a business day).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Create form */}
        <div className="xch-card p-5 mb-5" data-testid="xpay-new-form">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-4 h-4 text-indigo-700" />
            <div className="font-display font-bold">Create a payment link</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Send this link to your customer. They pay with their preferred method;
            Xchangely Pay collects the funds and settles you in USDC on your
            agreed payout schedule — you never need to share a wallet address
            with your customer.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" min="1" value={form.fromAmount}
                      onChange={(e) => setForm({ ...form, fromAmount: e.target.value })}
                      data-testid="xpay-form-amount" />
            </div>
            <div>
              <Label className="text-xs">Customer pays in</Label>
              <Select value={form.fromCurrency}
                       onValueChange={(v) => setForm({ ...form, fromCurrency: v })}
                       disabled={!!METHODS.find((m) => m.v === form.method)?.forceFrom}>
                <SelectTrigger className="rounded-xl" data-testid="xpay-form-from-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{FIAT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              {METHODS.find((m) => m.v === form.method)?.forceFrom && (
                <div className="text-[10px] text-muted-foreground mt-1" data-testid="xpay-form-currency-locked">
                  Locked to {METHODS.find((m) => m.v === form.method).forceFrom} for this method.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Payment method</Label>
              <Select value={form.method} onValueChange={(v) => {
                const meta = METHODS.find((m) => m.v === v);
                setForm((f) => ({
                  ...f,
                  method: v,
                  // Auto-swap fiat when the selected method forces a currency
                  // (PayID → AUD, Klarna → SEK). Saves the merchant a click and
                  // prevents the upstream rejecting the session.
                  fromCurrency: meta?.forceFrom || f.fromCurrency,
                }));
              }}>
                <SelectTrigger className="rounded-xl" data-testid="xpay-form-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METHODS.map((m) => (
                    <SelectItem key={m.v} value={m.v}>
                      <span className="flex items-center gap-2">
                        <span>{m.l}</span>
                        {m.note && <span className="text-[10px] text-muted-foreground">· {m.note}</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Your order reference (optional)</Label>
              <Input value={form.reference}
                      onChange={(e) => setForm({ ...form, reference: e.target.value })}
                      placeholder="INV-2026-0142"
                      data-testid="xpay-form-reference" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Customer email (optional — for receipts)</Label>
              <Input type="email" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="xpay-form-email" />
            </div>
          </div>
          <Button onClick={generate}
                  disabled={busy || !form.fromAmount || merchant.status !== "live"}
                  className="mt-4 rounded-xl gap-1.5"
                  data-testid="xpay-generate-btn">
            <Send className="w-4 h-4" />
            {merchant.status !== "live"
              ? "Awaiting activation"
              : busy ? "Generating…" : "Generate payment link"}
          </Button>
        </div>

        {lastLink && (
          <div className="xch-card p-5 mb-5 bg-emerald-50/40 border-emerald-200" data-testid="xpay-last-link">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-700" />
              <div className="font-display font-bold text-sm">Link ready — share this with your customer</div>
            </div>
            <code className="block bg-white border border-emerald-200 rounded-md px-3 py-2 text-[11px] break-all" data-testid="xpay-last-link-url">
              {lastLink.link_url}
            </code>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Button size="sm" onClick={() => copyLink(lastLink.link_url)} className="rounded-xl gap-1.5" data-testid="xpay-copy-btn">
                <Copy className="w-3.5 h-3.5" /> Copy link
              </Button>
              <Button size="sm" variant="outline" asChild className="rounded-xl gap-1.5">
                <a href={lastLink.link_url} target="_blank" rel="noopener noreferrer" data-testid="xpay-preview-btn">
                  <ExternalLink className="w-3.5 h-3.5" /> Preview
                </a>
              </Button>
            </div>
          </div>
        )}

        {/* Brand & receipts — co-branding upload (logo URL + support email).
            Visible to every approved/live merchant: customer receipts and
            the public checkout page show the merchant's logo at the top
            with "Receipt powered by Xchangely Pay" as the subtitle. */}
        <details className="xch-card mb-5 group" data-testid="xpay-brand-settings">
          <summary className="px-5 py-3.5 cursor-pointer flex items-center justify-between gap-2 list-none">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-indigo-700" />
              <span className="font-display font-bold">Brand &amp; receipts</span>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                · show your logo on the checkout page and customer receipts
              </span>
            </div>
            <ArrowDown className="w-3.5 h-3.5 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-5 pb-5 border-t border-border/50 pt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Logo URL (https only)</Label>
                <Input type="url" value={logoUrl}
                        onChange={(e) => setLogoUrl(e.target.value)}
                        placeholder="https://your-domain.com/logo.png"
                        data-testid="xpay-brand-logo-input" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  PNG, JPG, or SVG. We render it ~120px wide on receipts and
                  checkout. Must be served over HTTPS — HTTP images are
                  blocked by mail clients.
                </div>
              </div>
              <div>
                <Label className="text-xs">Customer support email (optional)</Label>
                <Input type="email" value={supportEmail}
                        onChange={(e) => setSupportEmail(e.target.value)}
                        placeholder="support@your-domain.com"
                        data-testid="xpay-brand-support-email-input" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  Shown on customer receipts so they email you directly with
                  order questions. Falls back to your account email if blank.
                </div>
              </div>
            </div>

            {/* Live preview — single source of truth for what the customer
                will see at the top of receipts + the checkout page. */}
            <div className="rounded-xl border border-border bg-muted/30 p-4"
                  data-testid="xpay-brand-preview">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
                Preview
              </div>
              <div className="flex items-center gap-3 pb-3 border-b border-border">
                {logoUrl ? (
                  <img src={logoUrl}
                        alt={merchant.business_name || "Merchant logo"}
                        key={logoUrl}
                        className="h-10 max-w-[120px] object-contain"
                        data-testid="xpay-brand-preview-logo"
                        // Hide image AND surface the inline error sibling
                        // via a class on the parent container. Driven by
                        // DOM rather than React state to keep this purely
                        // declarative under react-hooks/purity rules.
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const sib = e.currentTarget.parentElement?.parentElement?.querySelector("[data-testid='xpay-brand-preview-error']");
                          if (sib) sib.style.display = "block";
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.display = "block";
                          const sib = e.currentTarget.parentElement?.parentElement?.querySelector("[data-testid='xpay-brand-preview-error']");
                          if (sib) sib.style.display = "none";
                        }}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-indigo-700" />
                  </div>
                )}
                <div className="leading-tight">
                  <div className="font-display font-bold text-sm">
                    {merchant.business_name || "Your business"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-0.5">
                    Receipt powered by Xchangely Pay
                  </div>
                </div>
              </div>
              {logoUrl && (
                <div className="text-[11px] text-rose-700 mt-2"
                      style={{ display: "none" }}
                      data-testid="xpay-brand-preview-error">
                  Couldn&apos;t load that image. Double-check the URL is reachable
                  and serves a real image MIME (PNG / JPG / SVG).
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button onClick={saveBrand} disabled={brandBusy}
                       className="rounded-xl gap-1.5"
                       data-testid="xpay-brand-save">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {brandBusy ? "Saving…" : "Save brand settings"}
              </Button>
            </div>
          </div>
        </details>

        {/* Webhook settings — collapsible block above the links table.
            Hidden by default; only rendered if admin has flipped
            `api_access_enabled` on the merchant row. Most merchants don't
            need webhook access and shouldn't see the section. */}
        {merchant.api_access_enabled && (
        <details className="xch-card mb-5 group" data-testid="xpay-webhook-settings">
          <summary className="px-5 py-3.5 cursor-pointer flex items-center justify-between gap-2 list-none">
            <div className="flex items-center gap-2">
              <Webhook className="w-4 h-4 text-indigo-700" />
              <span className="font-display font-bold">Webhooks & API</span>
              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                · point your server at the URL below and we&apos;ll POST every status change
              </span>
            </div>
            <ArrowDown className="w-3.5 h-3.5 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="px-5 pb-5 border-t border-border/50 pt-4 space-y-4">
            {/* Callback URL */}
            <div>
              <Label className="text-xs">Your webhook URL</Label>
              <div className="flex gap-2">
                <Input type="url" value={callbackUrl} onChange={(e) => setCallbackUrl(e.target.value)}
                        placeholder="https://your-app.com/webhooks/xchangely"
                        data-testid="xpay-callback-url-input" />
                <Button onClick={saveCallbackUrl} disabled={secretBusy}
                        className="rounded-xl" data-testid="xpay-callback-url-save">
                  Save
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                We POST a JSON body when a link reaches a terminal status — completed,
                failed, cancelled, refunded. 3 retries, exponential back-off.
              </div>
            </div>
            {/* Webhook secret */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Key className="w-3 h-3" /> HMAC signing secret
              </Label>
              <div className="flex gap-2 items-center">
                <code className="flex-1 bg-muted border border-border rounded-md px-3 py-2 text-[11px] font-mono break-all"
                       data-testid="xpay-webhook-secret-display">
                  {webhookSecret == null ? "—" : (showSecret ? webhookSecret : maskedSecret(webhookSecret))}
                </code>
                <Button onClick={() => setShowSecret((s) => !s)} variant="ghost" size="sm"
                         className="rounded-xl"
                         data-testid="xpay-webhook-secret-toggle"
                         title={showSecret ? "Hide" : "Show"}>
                  {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
                <Button onClick={copySecret} variant="ghost" size="sm" className="rounded-xl"
                         disabled={!webhookSecret}
                         data-testid="xpay-webhook-secret-copy"
                         title="Copy">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button onClick={rotateSecret} variant="outline" size="sm"
                         disabled={secretBusy}
                         className="rounded-xl gap-1.5"
                         data-testid="xpay-webhook-secret-rotate">
                  <RefreshCw className="w-3.5 h-3.5" /> Rotate
                </Button>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                Verify each delivery by computing
                <code className="px-1 bg-muted text-foreground rounded mx-0.5">{`HMAC-SHA256(secret, "t=" + X-Xchangely-Pay-Timestamp + "." + raw_body)`}</code>
                and constant-time-comparing with the
                <code className="px-1 bg-muted text-foreground rounded mx-0.5">X-Xchangely-Pay-Signature</code>
                header (strip the <code className="px-1 bg-muted text-foreground rounded mx-0.5">sha256=</code> prefix).
              </div>
            </div>
          </div>
        </details>
        )}

        {/* Links table */}
        <div className="xch-card overflow-hidden" data-testid="xpay-links-list">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border gap-2 flex-wrap">
            <div className="font-display font-bold">Your payment links</div>
            <div className="flex items-center gap-1.5">
              <Button onClick={exportCsv} variant="ghost" size="sm"
                       className="rounded-xl gap-1.5 h-7"
                       data-testid="xpay-export-csv-btn"
                       disabled={links.length === 0}>
                <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Export CSV</span>
              </Button>
              <Button onClick={loadAll} variant="ghost" size="sm" className="rounded-xl gap-1.5 h-7"
                      data-testid="xpay-refresh-btn">
                <RefreshCw className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          <div className="divide-y divide-border">
            {links.length === 0 && (
              <div className="p-8 text-center" data-testid="xpay-links-empty">
                <div className="w-14 h-14 rounded-2xl mx-auto bg-indigo-50 border border-indigo-200 flex items-center justify-center mb-3">
                  <Sparkles className="w-6 h-6 text-indigo-700" />
                </div>
                <div className="font-display font-bold text-base mb-1">Your first link is one form away</div>
                <p className="text-xs text-muted-foreground max-w-sm mx-auto mb-4">
                  Use the form above to generate a payment link. Share it by email,
                  WhatsApp, or paste it on an invoice — Xchangely Pay handles
                  the rest. You&apos;ll see live status updates as soon as your
                  customer opens the link.
                </p>
                <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
                  <ArrowDown className="w-3.5 h-3.5 animate-bounce" /> Scroll up to create
                </div>
              </div>
            )}
            {links.map((l) => {
              const t = statusTone(l.status);
              const Icon = t.icon;
              return (
                <div key={l.id} className="px-5 py-3.5 flex items-center gap-3 flex-wrap"
                      data-testid={`xpay-row-${l.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-sm">
                        {l.amount} {l.from_currency}
                      </span>
                      <span className="text-[10px] text-muted-foreground">· {l.method}</span>
                      {l.reference && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground font-mono">
                          {l.reference}
                        </span>
                      )}
                    </div>
                    {l.customer_email && (
                      <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {l.customer_email}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Created {fmt(l.created_at)} · updated {fmt(l.updated_at)}
                    </div>
                  </div>
                  <Badge className={`text-[10px] uppercase tracking-wider gap-1 ${toneClass[t.tone]}`}
                          variant="outline" data-testid={`xpay-row-status-${l.id}`}>
                    <Icon className="w-3 h-3" /> {l.status}
                  </Badge>
                  <Button onClick={() => refreshOne(l.id)} variant="ghost" size="sm"
                          className="rounded-xl h-7 gap-1"
                          data-testid={`xpay-row-refresh-${l.id}`}>
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                  <Button onClick={() => copyLink(l.link_url)} variant="ghost" size="sm"
                          className="rounded-xl h-7 gap-1"
                          data-testid={`xpay-row-copy-${l.id}`}>
                    <Copy className="w-3 h-3" />
                  </Button>
                  {!isTerminal(l.status) && (
                    <Button onClick={() => cancelLink(l.id)} variant="ghost" size="sm"
                             className="rounded-xl h-7 gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                             data-testid={`xpay-row-cancel-${l.id}`}
                             title="Cancel this pending link">
                      <Ban className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}


// ============================================================
// Top-level shell
// ============================================================
export default function XPayPortal() {
  const [boot, setBoot] = useState(true);
  const [merchant, setMerchant] = useState(null);

  const tokenHeader = () => {
    const t = localStorage.getItem(TOK_KEY);
    return t ? { headers: { Authorization: `Bearer ${t}` } } : null;
  };

  const fetchMe = async () => {
    const t = localStorage.getItem(TOK_KEY);
    if (!t) { setMerchant(null); setBoot(false); return; }
    try {
      const r = await api.get("/xpay/merchant/me", tokenHeader());
      setMerchant(r.data?.merchant || null);
    } catch (e) {
      localStorage.removeItem(TOK_KEY);
      setMerchant(null);
    } finally { setBoot(false); }
  };
  useEffect(() => { fetchMe(); }, []);

  // Hijack axios default Authorization for xpay routes only, by injecting
  // the merchant token transparently. We do this by intercepting before each
  // /xpay request via an axios interceptor created here once.
  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      if (config.url && config.url.startsWith("/xpay/")) {
        const t = localStorage.getItem(TOK_KEY);
        if (t) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${t}`;
        }
      }
      return config;
    });
    return () => { api.interceptors.request.eject(id); };
  }, []);

  const logout = () => {
    localStorage.removeItem(TOK_KEY);
    setMerchant(null);
  };

  if (boot) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!merchant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <AuthShell onAuthed={setMerchant} />
      </div>
    );
  }

  if (!["approved", "live"].includes(merchant.status)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <PendingGate merchant={merchant} onLogout={logout} onRefresh={fetchMe} />
      </div>
    );
  }

  return <Dashboard merchant={merchant} onLogout={logout} onRefreshMerchant={fetchMe} />;
}
